'use client'

import { useRef } from 'react'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { useStackRecede } from './useStackRecede'
import '../styles/shipped-evidence.css'

// Showcase cards (origin -> connector -> adaptation) and the sticky
// top-to-bottom stack they scroll in, shared by the service pages
// (ShippedEvidenceSection) and the homepage (home-v2/ShowcaseStackV2).
// Kept free of the snapshot JSON import so the homepage bundle does not pull
// in every page's showcase data.

function isImageUrl(value) {
  return typeof value === 'string' && /^(https?:\/\/|\/)/i.test(value.trim())
}

// Pixel sizes of the self-hosted client logos in public/images/showcase, so
// each <img> carries width/height and the browser reserves its box before
// the file arrives. Logos hosted anywhere else simply go without.
const LOGO_SIZES = {
  'astral-system': [233, 90],
  'campbell-dermatology-aesthetics': [360, 83],
  'emerald-consulting-firm': [87, 90],
  'floor-daddy': [238, 90],
  'glscapes-inc': [332, 90],
  growtheon: [334, 90],
  'jack-ryan-group': [162, 90],
  'jordan-solender-coaching': [341, 53],
  'lisa-miller-relationship-coaching': [152, 90],
  'monkeyman-s-tree-service': [320, 90],
  'more-jobs-ai': [255, 90],
  'one-two-six-design': [69, 90],
  opsroi: [286, 90],
  'tetti-roofing': [75, 90],
  'tree-care-marketing-solutions': [263, 90],
  'webcare-digital': [332, 90],
}

function logoSize(url) {
  const match = /\/images\/showcase\/([a-z0-9-]+)\.webp$/i.exec(url)
  return (match && LOGO_SIZES[match[1]]) || null
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags.filter(Boolean)
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags)
      if (Array.isArray(parsed)) return parsed.filter(Boolean)
    } catch {
      return tags.split(',').map((t) => t.trim()).filter(Boolean)
    }
  }
  return []
}

// Renders the brand logo inside a fixed-height frame so the FULL logo is always
// visible (contain, never cropped), regardless of the brand's aspect ratio.
// Returns null when there is no icon, so the name simply leads instead.
function OriginLogo({ icon, name }) {
  if (!icon) return null
  if (isImageUrl(icon)) {
    const size = logoSize(icon)
    return (
      <div className="se-origin-logo-frame">
        <img
          className="se-origin-logo"
          src={icon}
          alt={name ? `${name} logo` : 'logo'}
          width={size?.[0]}
          height={size?.[1]}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.parentElement.style.display = 'none'
          }}
          onLoad={(e) => {
            if (!e.currentTarget.naturalWidth) e.currentTarget.parentElement.style.display = 'none'
          }}
        />
      </div>
    )
  }
  return (
    <div className="se-origin-logo-frame">
      <span className="se-origin-logo-emoji" aria-hidden="true">{icon}</span>
    </div>
  )
}

export function ShowcasePairContent({ item, index }) {
  const tags = normalizeTags(item.adaptation_tags)
  const originHref = item.origin_url
    ? (/^https?:\/\//i.test(item.origin_url) ? item.origin_url : `https://${item.origin_url}`)
    : null

  return (
    <>
      <div className="se-pair-origin">
        <span className="se-kicker">Consumer Origin</span>
        <OriginLogo icon={item.origin_icon} name={item.origin_name} />
        <strong className="se-origin-name">{item.origin_name}</strong>
        {originHref ? (
          <a className="se-origin-url" href={originHref} target="_blank" rel="noreferrer">
            {item.origin_url.replace(/^https?:\/\//i, '')} <ExternalLink size={12} aria-hidden="true" />
          </a>
        ) : null}
        {item.origin_description ? <p className="se-origin-desc">{item.origin_description}</p> : null}
        {item.origin_tagline ? <p className="se-origin-tagline">{item.origin_tagline}</p> : null}
      </div>

      <div className="se-pair-connector" aria-hidden="true">
        <span className="se-arrow"><ArrowRight size={18} /></span>
        <span className="se-index">{String(index + 1).padStart(2, '0')}</span>
      </div>

      <div className="se-pair-adaptation">
        <span className="se-adapt-badge">{item.adaptation_badge || 'Enterprise Adaptation'}</span>
        <h3 className="se-adapt-name">{item.adaptation_name}</h3>
        {item.adaptation_description ? <p className="se-adapt-desc">{item.adaptation_description}</p> : null}
        {tags.length > 0 ? (
          <div className="se-tags">
            {tags.map((tag) => (
              <span key={tag} className="se-tag">{tag}</span>
            ))}
          </div>
        ) : null}
      </div>
    </>
  )
}

export function ShowcaseStack({ items }) {
  const listRef = useRef(null)
  useStackRecede(listRef, items)
  if (!items?.length) return null
  return (
    <div className="se-pairs" ref={listRef}>
      {items.map((item, index) => (
        <article
          key={item.id}
          className="se-pair"
          data-rv="0.3"
          style={{ '--i': index, '--rv-y': '46px', '--rv-dur': '.75s' }}
        >
          <ShowcasePairContent item={item} index={index} />
        </article>
      ))}
    </div>
  )
}
